import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { User, Lock, Eye, EyeOff, LayoutDashboard, Users, FileText, KeyRound, ArrowLeft } from 'lucide-react';
import { adminLogin, forgotPassword, verifyForgotOTP, resetPassword } from '../../api/auth.api';
import { useAuth } from '../../context/AuthContext';
import { getLogoUrl } from '../../components/common/Logo';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import '../LoginPage.css';

const FEATURES = [
  { icon: LayoutDashboard, title: 'Dashboard Analytics',  desc: 'Real-time overview of all users and societies' },
  { icon: Users,           title: 'User Management',      desc: 'Create, configure, and control user access'  },
  { icon: FileText,        title: 'Billing & Invoicing',  desc: 'Automated invoices based on module usage'    },
];

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login, user, role } = useAuth();

  const [form,     setForm]     = useState({ userId: '', password: '' });
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [errors,   setErrors]   = useState({});

  // step: 'credentials' | 'forgot_userid' | 'forgot_otp' | 'forgot_reset'
  const [step,       setStep]       = useState('credentials');
  const [forgotId,   setForgotId]   = useState('');
  const [otp,        setOtp]        = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPass,    setNewPass]    = useState('');
  const [confirmPass,setConfirmPass]= useState('');
  const [subLoading, setSubLoading] = useState(false);

  if (user && role === 'admin') { navigate('/admin'); return null; }

  const validate = () => {
    const e = {};
    if (!form.userId.trim())   e.userId   = 'User ID is required';
    if (!form.password.trim()) e.password = 'Password is required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      const res = await adminLogin(form);
      login(res.data);
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    if (!forgotId.trim()) { toast.error('Enter your User ID'); return; }
    setSubLoading(true);
    try {
      const res = await forgotPassword({ userId: forgotId.trim(), userType: 'admin' });
      toast.success(res.data.message);
      setStep('forgot_otp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSubLoading(false);
    }
  };

  const handleForgotOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim()) { toast.error('Enter the OTP'); return; }
    setSubLoading(true);
    try {
      const res = await verifyForgotOTP({ userId: forgotId.trim(), userType: 'admin', otp: otp.trim() });
      setResetToken(res.data.resetToken);
      setStep('forgot_reset');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setSubLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPass.trim()) { toast.error('Enter a new password'); return; }
    if (newPass !== confirmPass) { toast.error('Passwords do not match'); return; }
    setSubLoading(true);
    try {
      await resetPassword({ resetToken, newPassword: newPass });
      toast.success('Password reset successfully. Please log in.');
      setStep('credentials');
      setForgotId(''); setOtp(''); setResetToken(''); setNewPass(''); setConfirmPass('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setSubLoading(false);
    }
  };

  const logoUrl = getLogoUrl();

  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-brand">
          <div className="login-logo-large">
            {logoUrl
              ? <img src={logoUrl} alt="Drusshti" className="login-logo-img" />
              : <div className="login-logo-fallback">D</div>
            }
            <div>
              <div className="login-brand-name">DRUSSHTI</div>
              <div className="login-brand-tagline">Managing Services the Smarter Way</div>
            </div>
          </div>
          <div className="login-features">
            {FEATURES.map(f => (
              <div key={f.title} className="login-feature">
                <div className="login-feature-icon"><f.icon size={16} /></div>
                <div className="login-feature-text">
                  <strong>{f.title}</strong>
                  <span>{f.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="login-form-wrap">

          {/* ── Login ── */}
          {step === 'credentials' && (
            <>
              <div className="login-form-header">
                <div className="login-role-pill"><Lock size={10} /> Admin Access</div>
                <h1>Admin Portal</h1>
                <p>Sign in to access the administration panel</p>
              </div>
              <form onSubmit={handleSubmit} className="login-form">
                <Input label="Admin User ID" id="userId" type="text" placeholder="e.g. ADMIN001"
                  value={form.userId} onChange={e => setForm(f => ({ ...f, userId: e.target.value }))}
                  error={errors.userId} iconLeft={User} required autoComplete="username" />
                <Input label="Password" id="password" type={showPass ? 'text' : 'password'}
                  placeholder="Enter your password" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  error={errors.password} iconLeft={Lock}
                  iconRight={showPass ? EyeOff : Eye} onIconRightClick={() => setShowPass(p => !p)}
                  required autoComplete="current-password" />
                <Button type="submit" loading={loading} size="lg" block className="login-submit-btn">
                  Sign in to Admin Panel
                </Button>
                <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                  <button type="button" onClick={() => setStep('forgot_userid')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--accent, #F5A623)', textDecoration: 'underline', opacity: 0.8 }}>
                    Forgot Password?
                  </button>
                </div>
              </form>
              <div className="login-footer-link">
                <a href="/user/login">Switch to User Portal →</a>
              </div>
            </>
          )}

          {/* ── Forgot — Enter User ID ── */}
          {step === 'forgot_userid' && (
            <>
              <div className="login-form-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <button type="button" onClick={() => setStep('credentials')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent, #F5A623)', display: 'flex', alignItems: 'center' }}>
                    <ArrowLeft size={16} />
                  </button>
                  <h1 style={{ margin: 0 }}>Forgot Password</h1>
                </div>
                <p>Enter your Admin User ID to receive an OTP on your registered email.</p>
              </div>
              <form onSubmit={handleForgotRequest} className="login-form">
                <Input label="Admin User ID" type="text" placeholder="e.g. ADMIN001"
                  value={forgotId} onChange={e => setForgotId(e.target.value)}
                  iconLeft={User} required />
                <Button type="submit" loading={subLoading} size="lg" block className="login-submit-btn">
                  Send OTP
                </Button>
              </form>
            </>
          )}

          {/* ── Forgot — Enter OTP ── */}
          {step === 'forgot_otp' && (
            <>
              <div className="login-form-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <button type="button" onClick={() => setStep('forgot_userid')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent, #F5A623)', display: 'flex', alignItems: 'center' }}>
                    <ArrowLeft size={16} />
                  </button>
                  <h1 style={{ margin: 0 }}>Enter OTP</h1>
                </div>
                <p>Check your registered email for the 6-digit OTP. Valid for 10 minutes.</p>
              </div>
              <form onSubmit={handleForgotOTP} className="login-form">
                <Input label="One-Time Password" type="text" placeholder="Enter 6-digit OTP"
                  value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  iconLeft={KeyRound} required autoComplete="one-time-code" inputMode="numeric" />
                <Button type="submit" loading={subLoading} size="lg" block className="login-submit-btn">
                  Verify OTP
                </Button>
                <div style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.85rem' }}>
                  <button type="button" onClick={handleForgotRequest}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', opacity: 0.7 }}>
                    Resend OTP
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ── Forgot — Reset Password ── */}
          {step === 'forgot_reset' && (
            <>
              <div className="login-form-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <KeyRound size={20} style={{ color: 'var(--accent, #F5A623)' }} />
                  <h1 style={{ margin: 0 }}>Set New Password</h1>
                </div>
                <p>Choose a strong password for your account.</p>
              </div>
              <form onSubmit={handleResetPassword} className="login-form">
                <Input label="New Password" type="password" placeholder="Min 6 characters"
                  value={newPass} onChange={e => setNewPass(e.target.value)}
                  iconLeft={Lock} required />
                <Input label="Confirm Password" type="password" placeholder="Re-enter new password"
                  value={confirmPass} onChange={e => setConfirmPass(e.target.value)}
                  iconLeft={Lock} required />
                <Button type="submit" loading={subLoading} size="lg" block className="login-submit-btn">
                  Reset Password
                </Button>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;